import { Box } from "@mui/material";
import type { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { formatVolume, NO_DATA_TEXT } from "../../utils/format";

export interface ConsumptionDetailRow {
	id: string;
	period: string;
	value: string;
}

const getColumnDefs = (unit: string): ColDef<ConsumptionDetailRow>[] => [
	{ headerName: "Period", field: "period", flex: 1, minWidth: 74 },
	{
		headerName: `Total (${unit})`,
		field: "value",
		flex: 1.2,
		minWidth: 96,
		cellStyle: {
			fontVariantNumeric: "tabular-nums",
			textAlign: "right",
		},
	},
];

const defaultColDef: ColDef<ConsumptionDetailRow> = {
	editable: false,
	filter: false,
	resizable: true,
	sortable: false,
	suppressHeaderMenuButton: true,
};

interface ConsumptionDetailGridProps {
	rows: ConsumptionDetailRow[];
	unit: string;
	total: number | null;
	onRowHover: (rowId: string | null) => void;
}

export const ConsumptionDetailGrid = ({
	rows,
	unit,
	total,
	onRowHover,
}: ConsumptionDetailGridProps) => {
	const pinnedBottomRowData: ConsumptionDetailRow[] = [
		{
			id: "total",
			period: "Total",
			value: formatVolume(total) ?? NO_DATA_TEXT,
		},
	];

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				"& .ag-row:hover": { backgroundColor: "#E3F0FB" },
				"& .ag-cell:focus, & .ag-header-cell:focus-within": {
					outline: "2px solid",
					outlineColor: "primary.main",
					outlineOffset: -2,
				},
				fontVariantNumeric: "tabular-nums",
			}}
		>
			<AgGridReact<ConsumptionDetailRow>
				rowData={rows}
				pinnedBottomRowData={pinnedBottomRowData}
				columnDefs={getColumnDefs(unit)}
				defaultColDef={defaultColDef}
				rowHeight={24}
				headerHeight={28}
				getRowStyle={(params) =>
					params.node.rowPinned ? { fontWeight: 700 } : undefined
				}
				getRowId={(params) => params.data.id}
				onCellMouseOver={(event) =>
					onRowHover(event.node.rowPinned ? null : (event.data?.id ?? null))
				}
				onCellMouseOut={() => onRowHover(null)}
				suppressContextMenu
			/>
		</Box>
	);
};
